import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../services/api';
import { useAuthStore, selectIsAdmin } from '../../stores/authStore';
import { PageHeader } from '../../components/common';
import { Button } from '../../components/ui/button';

const LIMIT = 20;

interface AdminUser {
  id: string;
  email: string;
  status: string;
  created_at: string;
  role?: string;
}

interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
}

const formatDate = (iso: string | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const AdminUserPage: React.FC = () => {
  const navigate = useNavigate();
  const isAdmin = useAuthStore(selectIsAdmin);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [hasHydrated, setHasHydrated] = useState(
    () => useAuthStore.persist.hasHydrated()
  );

  useEffect(() => {
    if (!hasHydrated) {
      const unsub = useAuthStore.persist.onFinishHydration(() => setHasHydrated(true));
      return () => unsub();
    }
  }, [hasHydrated]);

  useEffect(() => {
    if (hasHydrated && !isAdmin) {
      navigate('/forbidden');
    }
  }, [isAdmin, hasHydrated, navigate]);

  // debounce 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchUsers = useCallback(async (): Promise<AdminUsersResponse> => {
    const res = await api.get('/admin/users', {
      params: { page, limit: LIMIT, search: search || undefined },
    });
    return res.data.data ?? res.data;
  }, [page, search]);

  const { data, isLoading, isError } = useQuery<AdminUsersResponse>({
    queryKey: ['admin-users', page, search],
    queryFn: fetchUsers,
    enabled: !!isAdmin,
  });

  const total = data?.total ?? 0;
  const users = data?.users ?? [];
  const isPrevDisabled = page <= 1;
  const isNextDisabled = page * LIMIT >= total;

  return (
    <div className="p-6">
      <PageHeader title="ユーザー管理" />

      <div className="mb-4">
        <input
          data-testid="admin-user-search"
          type="text"
          placeholder="メールアドレスで検索..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="border rounded px-3 py-2 w-64 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {isError && (
        <div className="text-red-500 mb-4">ユーザー一覧の取得に失敗しました。</div>
      )}

      <div className="overflow-x-auto">
        <table data-testid="admin-user-table" className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 font-medium text-gray-600">メールアドレス</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">ステータス</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">作成日時</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">操作</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b">
                  <td className="px-4 py-3">
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-48" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-16" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-32" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-12" />
                  </td>
                </tr>
              ))
            ) : (
              users.map((user, index) => (
                <tr
                  key={user.id}
                  data-testid={`admin-user-row-${index}`}
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-gray-900">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(user.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      className="text-blue-600 hover:underline text-xs"
                      onClick={() => {}}
                    >
                      詳細
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button
          data-testid="admin-user-prev"
          variant="outline"
          size="sm"
          disabled={isPrevDisabled}
          onClick={() => setPage((p) => p - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
          前へ
        </Button>
        <span className="text-sm text-gray-600">
          {total > 0 ? `${(page - 1) * LIMIT + 1}–${Math.min(page * LIMIT, total)} / ${total}件` : '0件'}
        </span>
        <Button
          data-testid="admin-user-next"
          variant="outline"
          size="sm"
          disabled={isNextDisabled}
          onClick={() => setPage((p) => p + 1)}
        >
          次へ
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
